import { FunctionComponent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { UserContext } from '../../../context';
import {
  StoryCardStatus,
  StoryStatus,
  useStoriesByChannelIdQuery,
  useStoryCardsFeedQuery,
} from '../../../services';
import { IconLabel, LATEST_STORY_CARDS_STORAGE_KEY, Select, SelectItem } from '../../../shared';
import { RightSideStoryCard } from '../RightSideStoryCard';

import classes from './LatestsStoryCards.module.scss';

interface ConfigLatestStoryCard {
  channelId: number;
  storyIds: number[];
}

export interface ConfigLatestStoryCardsWidget {
  titleLocaliseKey: string;
  items: ConfigLatestStoryCard[];
  cardsLimit: number;
}

interface LatestsStoryCardsProps {
  config: ConfigLatestStoryCardsWidget;
}

export const LatestsStoryCards: FunctionComponent<LatestsStoryCardsProps> = ({
  config: { titleLocaliseKey, items, cardsLimit },
}) => {
  const { t } = useTranslation();

  const { channelId, channelName } = useContext(UserContext).userInfo.body;

  const [selectedStoryId, setSelectedStoryId] = useState<number>(0);

  const channelSetup = useMemo(
    () => items.find(({ channelId: id }) => id === channelId),
    [channelId, items]
  );

  const storyIds = useMemo(() => channelSetup?.storyIds ?? [], [channelSetup?.storyIds]);

  const { data: stories, isFetching: isFetchingStories } = useStoriesByChannelIdQuery({
    channelId,
    filter: { onlyPostIn: false, status: [StoryStatus.PUBLISHED] },
  });

  const filteredStories = useMemo(() => {
    // if no storyIds provided, show all stories
    if (!Boolean(storyIds.length)) {
      return stories;
    }

    return stories?.filter(({ id }) => storyIds.includes(id));
  }, [stories, storyIds]);

  const storageKey = useMemo(
    () => `${LATEST_STORY_CARDS_STORAGE_KEY}:${channelName}:${storyIds.join(',')}`,
    [channelName, storyIds]
  );

  const storedStoryId = useMemo(
    () => parseInt(localStorage.getItem(storageKey) ?? '', 10),
    [storageKey]
  );

  const fallbackStoryId = useMemo(() => filteredStories?.[0]?.id, [filteredStories]);

  const storyOptions = useMemo(() => {
    return filteredStories?.map(({ id, title }) => {
      return { id, title } as SelectItem;
    });
  }, [filteredStories]);

  useEffect(() => {
    if (!filteredStories) {
      return;
    }

    if (filteredStories?.find(({ id }) => id === storedStoryId)) {
      setSelectedStoryId(storedStoryId);
      return;
    }

    localStorage.removeItem(storageKey);

    if (fallbackStoryId) {
      setSelectedStoryId(fallbackStoryId);
    }
  }, [fallbackStoryId, filteredStories, storageKey, storedStoryId]);

  const onStoryChange = useCallback(
    (storyId: number) => {
      localStorage.setItem(storageKey, storyId.toString());
      setSelectedStoryId(storyId);
    },
    [storageKey]
  );

  const { data: cardsData, isFetching: isFetchingCards } = useStoryCardsFeedQuery(
    {
      storyId: selectedStoryId,
      size: cardsLimit,
      filter: { onlyCompletedMedia: true, status: [StoryCardStatus.PUBLISHED] },
    },
    { skip: !selectedStoryId }
  );

  const renderCards = useMemo(() => {
    const cards = cardsData?.items ?? [];

    if (isFetchingCards) {
      return (
        <Skeleton className={classes['latest-story-cards__cards-loader']} count={cardsLimit} />
      );
    }

    if (!Boolean(cards.length)) {
      return (
        <div className={classes['latest-story-cards__empty']}>
          <IconLabel
            className={classes['latest-story-cards__empty-icon']}
            iconId={'image'}
            iconSize={32}
            color={'#98A1A6'}
            nonClickable
          />
          {t('latestsStoryCards.no-content')}
        </div>
      );
    }

    return cards.map((card) => <RightSideStoryCard key={card.id} card={card} />);
  }, [cardsData?.items, cardsLimit, isFetchingCards, t]);

  if (isFetchingStories) {
    return <Skeleton width={'18.75rem'} height={'16rem'} />;
  }

  if (!filteredStories?.length) {
    return null;
  }

  return (
    <div className={classes['latest-story-cards']}>
      {Boolean(selectedStoryId) && storyOptions && (
        <Select
          selectedItemId={selectedStoryId}
          label={t(titleLocaliseKey ?? 'latestsStoryCards.select-title')}
          items={storyOptions}
          onChange={(storyId) => onStoryChange(storyId as number)}
          disabled={Boolean(storyOptions.length === 1)}
          noBorder
        />
      )}
      {renderCards}
    </div>
  );
};
